/**
 * Who sees what — the frontend side of the rights in the API.
 *
 * Since 2026-11-05 what a role may do is in the database (Beheer › Rechten,
 * docs/auth.md). The session (`GET /auth/me`) carries this user's rights; admin has
 * them all. This file decides **nothing**: every endpoint has its own
 * `requirePermission` in the API. It only keeps someone from seeing a menu item or
 * button that would answer 403. When the two disagree, the API wins.
 *
 * The keys below are a copy of `services/api/src/lib/permissionCatalog.ts`;
 * services/api/test/permissions.test.ts compares them, so a new key cannot stay on
 * one side.
 */

export const PERMISSIONS = [
  "proformas",
  "proformas_write",
  "proformas_manual_invoice",
  "proformas_lead_access",
  "open_orders",
  "open_orders_notes",
  "open_orders_notes_delete_any",
  "planning",
  "planning_sync",
  "parties",
  "parties_write",
  "email_addresses",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** What a gate asks: one of these rights, or beheer itself. */
export type Access = readonly Permission[] | "admin";

/** The part of the session this file reads. */
export interface AccessSession {
  roles: readonly string[];
  permissions?: readonly string[];
}

/** Beheer: always everything, and the only one who hands out access. */
export function isAdmin(session: AccessSession | null | undefined): boolean {
  return !!session?.roles.includes("admin");
}

/** Does this session hold one of these rights? */
export function can(session: AccessSession | null | undefined, ...keys: Permission[]): boolean {
  if (!session) return false;
  if (isAdmin(session)) return true;
  return keys.some((k) => session.permissions?.includes(k));
}

export function mayAccess(session: AccessSession | null | undefined, access: Access): boolean {
  return access === "admin" ? isAdmin(session) : can(session, ...access);
}
